import { ITaxDashboardByStoreItem, ITaxDashboardByUFItem } from '@/@types/tax-dashboard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLazyGetTaxDashboardByStoreQuery, useLazyGetTaxDashboardByUFQuery, useLazyGetTaxDashboardQuery } from '@/services/tax-dashboard';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import ConciliacaoFiscalTab from "./components/conciliacao-fiscal";
import MigrateTab from "./components/migrate";

const TaxReconciliationTab = lazy(() => import("./components/tax-reconciliation"));
import {
  DashboardFilters,
  NFCeChart,
  UFTable,
  StoreTable,
  MetricsGrid,
  transformData,
  transformDataByUF,
  transformDataByStore,
  getStoresByUF,
  StoreUF
} from "./components/nfce-dashboard";

const EngineDashboard = () => {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [dashboardData, setDashboardData] = useState<any[]>([]);
  const [dashboardByUFData, setDashboardByUFData] = useState<ITaxDashboardByUFItem[]>([]);
  const [dashboardByStoreData, setDashboardByStoreData] = useState<ITaxDashboardByStoreItem[]>([]);
  const [groupByMonth, setGroupByMonth] = useState(false);

  const [trigger, { isFetching }] = useLazyGetTaxDashboardQuery();
  const [triggerByUF, { isFetching: isFetchingByUF }] = useLazyGetTaxDashboardByUFQuery();
  const [triggerByStore, { isFetching: isFetchingByStore }] = useLazyGetTaxDashboardByStoreQuery();

  const hadleSerchButton = useCallback(async () => {
    const [response, responseByUF, responseByStore] = await Promise.all([
      trigger({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        stores: selectedStores,
        groupByMonth
      }),
      triggerByUF({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      }),
      triggerByStore({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      })
    ]);

    setDashboardData(response.data || []);
    setDashboardByUFData(responseByUF.data || []);
    setDashboardByStoreData(responseByStore.data || []);
  }, [startDate, endDate, selectedStores, groupByMonth, trigger, triggerByUF, triggerByStore]);

  const chartData = useMemo(() => transformData(dashboardData), [dashboardData]);
  const tableData = useMemo(() => transformDataByUF(dashboardByUFData), [dashboardByUFData]);
  const storeTableData = useMemo(() => transformDataByStore(dashboardByStoreData), [dashboardByStoreData]);

  const handleStoreSelect = useCallback((storeId: string) => {
    setSelectedStores(prev => {
      if (prev.includes(storeId)) {
        return prev.filter(id => id !== storeId);
      }
      return [...prev, storeId];
    });
  }, []);

  const handleUFSelect = useCallback((uf: StoreUF, checked: boolean) => {
    const ufStores = Array.from(getStoresByUF().get(uf) || []);

    setSelectedStores(prev => {
      if (checked) {
        const storesToAdd = ufStores.filter(store => !prev.includes(store));
        return [...prev, ...storesToAdd];
      } else {
        return prev.filter(store => !ufStores.includes(store));
      }
    });
  }, []);

  const handleRemoveStore = useCallback((storeId: string) => {
    setSelectedStores(prev => prev.filter(id => id !== storeId));
  }, []);

  const handleClearFilters = useCallback(() => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedStores([]);
    setGroupByMonth(false);
  }, []);

  return (
    <PageLayout>
      <PageTitle title="Monitoramento de NFCe" />
      <ContentContainer>
        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="mb-8 h-12 bg-white border-b-2 border-gray-200 rounded-none justify-start p-0">
            <TabsTrigger
              value="dashboard"
              className="h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none font-semibold"
            >
              Dashboard NFCe
            </TabsTrigger>
            <TabsTrigger
              value="conciliacao-fiscal"
              className="h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none font-semibold"
            >
              Conciliação Fiscal v1
            </TabsTrigger>
            <TabsTrigger
              value="tax-reconciliation"
              className="h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none font-semibold"
            >
              Conciliação Fiscal v2
            </TabsTrigger>
            <TabsTrigger
              value="migrate"
              className="h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none font-semibold"
            >
              Migrate
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardFilters
              startDate={startDate}
              endDate={endDate}
              selectedStores={selectedStores}
              groupByMonth={groupByMonth}
              isFetching={isFetching}
              onStartDateChange={(date) => setStartDate(date || undefined)}
              onEndDateChange={(date) => setEndDate(date || undefined)}
              onStoreSelect={handleStoreSelect}
              onUFSelect={handleUFSelect}
              onGroupByMonthChange={setGroupByMonth}
              onRemoveStore={handleRemoveStore}
              onClearFilters={handleClearFilters}
              onSearch={hadleSerchButton}
            />

            {/* Key Metrics */}
            <MetricsGrid
              chartData={chartData}
              ufData={tableData}
              storeData={storeTableData}
              isFetching={isFetching || isFetchingByUF || isFetchingByStore}
            />

            <div className="grid gap-6">
              <NFCeChart
                data={chartData}
                isFetching={isFetching}
              />

              <div className="grid grid-cols-2 gap-6">
                <UFTable
                  data={tableData}
                  isFetching={isFetchingByUF}
                />

                <StoreTable
                  data={storeTableData}
                  isFetching={isFetchingByStore}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="conciliacao-fiscal">
            <ConciliacaoFiscalTab />
          </TabsContent>

          <TabsContent value="tax-reconciliation">
            <Suspense fallback={<div className="flex items-center justify-center py-12">Loading...</div>}>
              <TaxReconciliationTab />
            </Suspense>
          </TabsContent>

          <TabsContent value="migrate">
            <MigrateTab />
          </TabsContent>

        </Tabs>
      </ContentContainer>
    </PageLayout>
  );
};

export { EngineDashboard };